import React, { useEffect, useRef } from 'react'
import {
    APIProvider,
    Map,
    useMap,
    useMapsLibrary,
} from '@vis.gl/react-google-maps'
import { MarkerClusterer } from '@googlemaps/markerclusterer'
import simpleheat from 'simpleheat'
import { PIN_COLORS } from '../checkin/pinStyle'

/**
 * Shared map renderer for Places data. The PlacesCollectionPage and the
 * per-project Places section both feed this the same shape: a list of named
 * Places plus a list of coords-only "unnamed pins" (Locations features with
 * lat/lng but no reference_value). Two render modes:
 *
 *  - heatmap:  density gradient — "where do I go a lot"
 *  - clusters: grouped markers — "find a specific pin"
 *
 *  The component handles loading state and the missing-Google-key fallback
 *  internally so callers only need to manage data + view mode.
 */

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY as string | undefined
// Map ID registered in Google Cloud Console (Maps Platform → Map
// Management). Required by `AdvancedMarkerElement` used in cluster view;
// any valid registered Map ID (raster or vector) works for the
// simpleheat OverlayView heatmap. Currently a vector basemap — slightly
// nicer label antialiasing than the default raster style.
const PLACES_MAP_ID = '73429d23dffd52608183b1ff'

export interface PlaceMapItem {
    id: number
    name: string
    lat: number | null
    lng: number | null
    address: string | null
    google_place_id: string | null
    source: 'user' | 'google' | null
    category_id: number | null
    category_name: string | null
    category_slug: string | null
    notes: string | null
    checkin_count: number
}

export interface UnnamedPin {
    lat: number
    lng: number
}

export type PlacesMapViewMode = 'heatmap' | 'clusters'

interface PlacesMapProps {
    places: PlaceMapItem[]
    unnamedPins: UnnamedPin[]
    viewMode: PlacesMapViewMode
    loading?: boolean
    onPlaceClick?: (p: PlaceMapItem) => void
    onPinClick?: (c: { lat: number; lng: number }) => void
    /** Tailwind height utility. Defaults to h-[420px]. */
    heightClass?: string
    /** Override the rendered "no data" message in cluster mode. The heatmap
     *  is still mounted in either case so the bounds-fit is visible. */
    emptyMessage?: string
}

const PlacesMap: React.FC<PlacesMapProps> = ({
    places,
    unnamedPins,
    viewMode,
    loading = false,
    onPlaceClick,
    onPinClick,
    heightClass = 'h-[420px]',
}) => {
    if (!GOOGLE_MAPS_API_KEY) {
        return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                Map unavailable — VITE_GOOGLE_MAPS_BROWSER_KEY is not set.
            </div>
        )
    }

    return (
        <div className={`${heightClass} relative`}>
            {loading ? (
                <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                    Loading map…
                </div>
            ) : (
                // Mount Map only after loading completes — <Map> treats
                // defaultCenter/defaultZoom as one-shot, so mounting on an
                // empty set would freeze at lat 0,0 even after data arrives.
                <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['marker']}>
                    <Map
                        style={{ width: '100%', height: '100%' }}
                        {...mapInitialView(places, unnamedPins)}
                        mapId={PLACES_MAP_ID}
                        gestureHandling="greedy"
                        disableDefaultUI={false}
                        // Google renders interactive POIs (restaurants, bars,
                        // landmarks) on the basemap by default; tapping one
                        // pops Google's own InfoWindow ("View on Google Maps"
                        // etc.) which is jarring next to our pin tooltips.
                        // Suppress them entirely — the user is here to see
                        // their own Places, not browse Google's index.
                        clickableIcons={false}
                        // Vector basemap enables tilt + bearing by default;
                        // we want a flat, north-up view to match how the
                        // raster version behaved. `isFractionalZoomEnabled`
                        // off keeps the default Google Maps stepped zoom
                        // (vector turns it on by default).
                        tilt={0}
                        heading={0}
                        rotateControl={false}
                    >
                        {viewMode === 'heatmap' ? (
                            <HeatmapLayer places={places} pins={unnamedPins} />
                        ) : (
                            <ClusterLayer
                                places={places}
                                pins={unnamedPins}
                                onPlaceClick={onPlaceClick}
                                onPinClick={onPinClick}
                            />
                        )}
                    </Map>
                </APIProvider>
            )}
        </div>
    )
}

// Heatmap renderer. Google removed `google.maps.visualization.HeatmapLayer`
// in Maps JS v3.65 (it now throws on construction). This replacement uses
// simpleheat (Mapbox's tiny canvas heatmap, ~3KB — what Leaflet.heat is
// built on) inside a `google.maps.OverlayView` mounted in `mapPane` — so
// the canvas CSS-scales with the basemap during zoom gestures (smooth, but
// briefly blurry from the transform) and redraws crisp at the new pixel
// dimensions when the projection settles and `draw()` fires.
type HeatmapPoint = { lat: number; lng: number; weight: number }

const HeatmapLayer: React.FC<{
    places: PlaceMapItem[]
    pins: UnnamedPin[]
}> = ({ places, pins }) => {
    const map = useMap()

    useEffect(() => {
        if (!map) return

        const points: HeatmapPoint[] = []
        for (const p of places) {
            if (p.lat == null || p.lng == null) continue
            // Floor at 1 so a place with no checkins still contributes
            // a baseline to the footprint — otherwise newly-saved Places
            // would silently vanish from the heatmap.
            points.push({ lat: p.lat, lng: p.lng, weight: Math.max(1, p.checkin_count) })
        }
        for (const pin of pins) {
            points.push({ lat: pin.lat, lng: pin.lng, weight: 1 })
        }

        // Dev knob: inflate the heatmap input to preview what it would look
        // like at higher pin density. Drives off URL query params, not data
        // or persisted state: e.g. /…?heat_mul=5&heat_jitter=0.001 duplicates
        // each point 4 extra times, jittering lat/lng by up to ±0.001° (~110m
        // at NYC latitude). Cluster view ignores this — purely a visual
        // sandbox for tuning the heatmap. Delete once the look is settled.
        const params = new URLSearchParams(window.location.search)
        const heatMul = Math.max(1, Math.floor(Number(params.get('heat_mul')) || 1))
        const heatJitter = Math.max(0, Number(params.get('heat_jitter')) || 0.0005)
        if (heatMul > 1) {
            // Gaussian jitter (Box-Muller) rather than uniform-square: most
            // duplicates land near the original, a few wander farther. heatJitter
            // is the stddev, so ~68% fall within ±heatJitter and ~95% within ±2x.
            // Looks more organic than a tight rectangular halo around each pin.
            const gauss = () => {
                const u1 = Math.max(Number.EPSILON, Math.random())
                const u2 = Math.random()
                return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2)
            }
            const originals = points.slice()
            for (let i = 1; i < heatMul; i++) {
                for (const p of originals) {
                    points.push({
                        lat: p.lat + gauss() * heatJitter,
                        lng: p.lng + gauss() * heatJitter,
                        weight: p.weight,
                    })
                }
            }
        }

        if (points.length === 0) return

        const maxWeight = points.reduce((m, p) => Math.max(m, p.weight), 0)

        // Class declared inside the effect because `google.maps.OverlayView`
        // only exists after the Maps JS API has loaded — module-scope
        // `extends google.maps.OverlayView` would crash at import time.
        class HeatmapOverlay extends google.maps.OverlayView {
            private canvas: HTMLCanvasElement | null = null
            private heat: ReturnType<typeof simpleheat> | null = null

            onAdd() {
                this.canvas = document.createElement('canvas')
                this.canvas.style.position = 'absolute'
                this.canvas.style.pointerEvents = 'none'
                // mapPane is the bottom-most pane and scales/rotates with
                // the basemap, which is exactly what we want during zoom.
                this.getPanes()!.mapPane.appendChild(this.canvas)
            }

            draw() {
                if (!this.canvas) return
                const projection = this.getProjection()
                if (!projection) return
                const bounds = map.getBounds()
                if (!bounds) return

                // Size the canvas to the visible viewport, not the bounding
                // box of all points. simpleheat's draw() iterates every pixel
                // of the canvas (getImageData → colorize loop → putImageData),
                // and div-pixel coordinates from fromLatLngToDivPixel scale
                // ~2× per zoom level — so a footprint-sized canvas grows ~4×
                // in area per zoom step and becomes billions of pixels at
                // street zooms, eating seconds per frame. Viewport-sized
                // canvas is constant per frame regardless of zoom.
                const RADIUS = 18
                const BLUR = 15
                const PAD = RADIUS + BLUR
                const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast())
                const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest())
                if (!ne || !sw) return
                // In div-pixel space, x grows east and y grows south, so the
                // NE corner is (maxX, minY) and SW is (minX, maxY). Pad each
                // side by PAD so blobs at the viewport edge aren't clipped.
                const minX = sw.x - PAD
                const maxX = ne.x + PAD
                const minY = ne.y - PAD
                const maxY = sw.y + PAD

                // Project + filter to points whose draw circle can touch the
                // viewport. A point at p paints pixels in p ± PAD, so any
                // point within the padded canvas bounds is in scope; anything
                // beyond would paint entirely outside the visible area.
                const pixelCoords: Array<[number, number, number]> = []
                for (const pt of points) {
                    const px = projection.fromLatLngToDivPixel(
                        new google.maps.LatLng(pt.lat, pt.lng),
                    )
                    if (!px) continue
                    if (px.x < minX || px.x > maxX) continue
                    if (px.y < minY || px.y > maxY) continue
                    pixelCoords.push([px.x, px.y, pt.weight])
                }
                if (pixelCoords.length === 0) {
                    // No points in view — collapse the canvas so any prior
                    // frame's pixels don't linger.
                    this.canvas.width = 0
                    this.canvas.height = 0
                    return
                }

                const width = Math.ceil(maxX - minX)
                const height = Math.ceil(maxY - minY)

                this.canvas.style.left = `${minX}px`
                this.canvas.style.top = `${minY}px`
                // Setting canvas.width/height also clears the canvas, so do
                // it unconditionally; simpleheat's resize() picks up the
                // new dimensions.
                this.canvas.width = width
                this.canvas.height = height

                if (!this.heat) {
                    this.heat = simpleheat(this.canvas)
                    this.heat.radius(RADIUS, BLUR)
                } else {
                    this.heat.resize()
                }

                this.heat.max(maxWeight)
                this.heat.data(
                    pixelCoords.map(
                        ([x, y, v]) =>
                            [Math.round(x - minX), Math.round(y - minY), v] as [
                                number,
                                number,
                                number,
                            ],
                    ),
                )
                this.heat.draw(0.05)
            }

            onRemove() {
                this.canvas?.remove()
                this.canvas = null
                this.heat = null
            }
        }

        const overlay = new HeatmapOverlay()
        overlay.setMap(map)
        return () => {
            overlay.setMap(null)
        }
    }, [map, places, pins])

    return null
}

const ClusterLayer: React.FC<{
    places: PlaceMapItem[]
    pins: UnnamedPin[]
    onPlaceClick?: (p: PlaceMapItem) => void
    onPinClick?: (c: { lat: number; lng: number }) => void
}> = ({ places, pins, onPlaceClick, onPinClick }) => {
    const map = useMap()
    const markerLib = useMapsLibrary('marker')
    const clustererRef = useRef<MarkerClusterer | null>(null)
    const overlayRef = useRef<google.maps.OverlayView | null>(null)
    // Mixed kinds: named places use AdvancedMarkerElement (so they pick up
    // the standardized red+white-dot Pin shared with the rest of the app);
    // unnamed pins stay as classic Marker with a custom gray-circle icon
    // because that's a deliberate visual distinction (no Place row backs
    // them) and AdvancedMarker has no equivalent simple-symbol shortcut.
    const markersRef = useRef<Array<google.maps.Marker | google.maps.marker.AdvancedMarkerElement>>([])

    useEffect(() => {
        if (!map || !markerLib) return

        if (clustererRef.current) {
            clustererRef.current.clearMarkers()
            clustererRef.current = null
        }
        overlayRef.current?.setMap(null)
        overlayRef.current = null
        for (const m of markersRef.current) {
            if (m instanceof google.maps.Marker) m.setMap(null)
            else m.map = null
        }
        markersRef.current = []

        // Custom overlay subclass. Declared inside the effect because
        // `google.maps.OverlayView` only exists after the Maps JS API has
        // loaded — module-scope class extends would crash at import time.
        // We render bubble + tail into the `floatPane` so it z-orders above
        // markers and routes pointer events normally.
        class TooltipOverlay extends google.maps.OverlayView {
            private container: HTMLDivElement
            private latLng: google.maps.LatLng
            constructor(position: { lat: number; lng: number }, content: HTMLElement) {
                super()
                this.latLng = new google.maps.LatLng(position)
                this.container = document.createElement('div')
                this.container.style.position = 'absolute'
                // Anchor by bottom-center: bubble sits above the marker pixel,
                // with a small gap reserved for the tail. `draw()` writes
                // `left`/`top`; this transform pulls the box up + center.
                this.container.style.transform = 'translate(-50%, calc(-100% - 14px))'
                this.container.appendChild(content)
            }
            onAdd() {
                this.getPanes()!.floatPane.appendChild(this.container)
            }
            onRemove() {
                this.container.remove()
            }
            draw() {
                const projection = this.getProjection()
                if (!projection) return
                const px = projection.fromLatLngToDivPixel(this.latLng)
                if (!px) return
                this.container.style.left = `${px.x}px`
                this.container.style.top = `${px.y}px`
            }
        }

        const closeOverlay = () => {
            overlayRef.current?.setMap(null)
            overlayRef.current = null
        }
        const openOverlay = (position: { lat: number; lng: number }, content: HTMLElement) => {
            closeOverlay()
            const o = new TooltipOverlay(position, content)
            o.setMap(map)
            overlayRef.current = o
        }

        // Map-click dismissal — friendlier on mobile than a precise X tap.
        // The bubble itself stops propagation so taps inside don't trigger
        // this. Marker clicks fire `gmp-click`/`click` on the marker, not on
        // the map, so opening a new tooltip doesn't race with this listener.
        const mapClickListener = map.addListener('click', closeOverlay)

        const markers: Array<google.maps.Marker | google.maps.marker.AdvancedMarkerElement> = []
        for (const p of places) {
            if (p.lat == null || p.lng == null) continue
            const pin = new markerLib.PinElement({
                background: PIN_COLORS.bg,
                borderColor: PIN_COLORS.border,
                glyphColor: PIN_COLORS.glyph,
            })
            const marker = new markerLib.AdvancedMarkerElement({
                position: { lat: p.lat, lng: p.lng },
                content: pin.element,
                title: p.name,
                gmpClickable: true,
            })
            marker.addListener('click', () => {
                openOverlay(
                    { lat: p.lat!, lng: p.lng! },
                    buildTooltip(
                        p.name,
                        closeOverlay,
                        onPlaceClick ? () => {
                            closeOverlay()
                            onPlaceClick(p)
                        } : undefined,
                    ),
                )
            })
            markers.push(marker)
        }
        for (const pin of pins) {
            const coords = { lat: pin.lat, lng: pin.lng }
            const marker = new google.maps.Marker({
                position: coords,
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 5,
                    fillColor: '#6B7280',
                    fillOpacity: 0.85,
                    strokeColor: '#374151',
                    strokeWeight: 1,
                },
            })
            marker.addListener('click', () => {
                openOverlay(
                    coords,
                    buildTooltip(
                        'Unnamed location',
                        closeOverlay,
                        onPinClick ? () => {
                            closeOverlay()
                            onPinClick(coords)
                        } : undefined,
                    ),
                )
            })
            markers.push(marker)
        }
        markersRef.current = markers

        clustererRef.current = new MarkerClusterer({ map, markers })

        return () => {
            google.maps.event.removeListener(mapClickListener)
            closeOverlay()
            clustererRef.current?.clearMarkers()
            clustererRef.current = null
            for (const m of markers) {
                if (m instanceof google.maps.Marker) m.setMap(null)
                else m.map = null
            }
        }
    }, [map, markerLib, places, pins, onPlaceClick, onPinClick])

    return null
}

// Build the tooltip DOM imperatively — it's mounted into the OverlayView's
// floatPane, outside the React tree. Tailwind utilities still apply (global
// stylesheet); they just can't flow through JSX. Structure is
// `<wrapper>(<bubble><tail/></bubble>)`: the wrapper stops click propagation
// (so taps inside the bubble don't trigger the map's close-on-click), the
// bubble holds the visible card, and the tail is a CSS triangle stuck to
// the bubble's bottom-center.
function buildTooltip(name: string, onClose: () => void, onView?: () => void): HTMLElement {
    const wrapper = document.createElement('div')
    wrapper.style.position = 'relative'
    wrapper.addEventListener('click', (e) => e.stopPropagation())

    const bubble = document.createElement('div')
    bubble.className =
        'bg-white rounded-lg shadow-lg border border-gray-200 px-3 py-2 min-w-[140px] max-w-[240px]'
    wrapper.appendChild(bubble)

    const headerRow = document.createElement('div')
    headerRow.className = 'flex items-start gap-2'
    bubble.appendChild(headerRow)

    const nameEl = document.createElement('div')
    nameEl.className = 'font-medium text-gray-900 text-sm break-words flex-1 leading-snug'
    nameEl.textContent = name
    headerRow.appendChild(nameEl)

    const closeBtn = document.createElement('button')
    closeBtn.type = 'button'
    closeBtn.setAttribute('aria-label', 'Close')
    closeBtn.className =
        'shrink-0 text-gray-400 hover:text-gray-600 leading-none cursor-pointer text-base -mr-1'
    closeBtn.textContent = '×'
    closeBtn.addEventListener('click', onClose)
    headerRow.appendChild(closeBtn)

    if (onView) {
        const link = document.createElement('button')
        link.type = 'button'
        link.className = 'mt-1 text-sky-600 hover:text-sky-700 text-sm font-medium cursor-pointer'
        link.textContent = 'View details'
        link.addEventListener('click', onView)
        bubble.appendChild(link)
    }

    // Tail — downward-pointing triangle anchored to the bubble's bottom-center.
    // 1px outer offset gives the illusion of the bubble's border running
    // around the triangle as well (close enough at this size).
    const tail = document.createElement('div')
    tail.style.position = 'absolute'
    tail.style.left = '50%'
    tail.style.bottom = '-7px'
    tail.style.transform = 'translateX(-50%)'
    tail.style.width = '0'
    tail.style.height = '0'
    tail.style.borderLeft = '7px solid transparent'
    tail.style.borderRight = '7px solid transparent'
    tail.style.borderTop = '7px solid white'
    tail.style.filter = 'drop-shadow(0 1px 0.5px rgb(0 0 0 / 0.12))'
    bubble.appendChild(tail)

    return wrapper
}

/** Initial Map view computed from the data set. Bounds-fit over centroid:
 *  arithmetic means get yanked by outliers, and the goal of the first frame
 *  is "see the whole footprint", not "land near the densest cluster".
 *
 *  Edge cases:
 *    - No coords → wide world view (lat 0, lng 0, zoom 2).
 *    - One coord (or all coords near-coincident) → defaultCenter + zoom 14;
 *      bounds-fit with one point auto-zooms to a single tile and loses
 *      context.
 */
function mapInitialView(
    places: PlaceMapItem[],
    pins: UnnamedPin[],
): {
    defaultCenter?: { lat: number; lng: number }
    defaultZoom?: number
    defaultBounds?: google.maps.LatLngBoundsLiteral
} {
    const lats: number[] = []
    const lngs: number[] = []
    for (const p of places) {
        if (p.lat != null && p.lng != null) {
            lats.push(p.lat)
            lngs.push(p.lng)
        }
    }
    for (const pin of pins) {
        lats.push(pin.lat)
        lngs.push(pin.lng)
    }
    if (lats.length === 0) {
        return { defaultCenter: { lat: 0, lng: 0 }, defaultZoom: 2 }
    }
    const north = Math.max(...lats)
    const south = Math.min(...lats)
    const east = Math.max(...lngs)
    const west = Math.min(...lngs)
    if (north - south < 0.001 && east - west < 0.001) {
        return {
            defaultCenter: { lat: (north + south) / 2, lng: (east + west) / 2 },
            defaultZoom: 14,
        }
    }
    return {
        defaultBounds: { north, south, east, west },
    }
}

export default PlacesMap
